import {
  type AutomationTrigger,
  AutomationTriggerType,
  type SendMessageMultichannelActionOptions,
} from "@/api/types/automation";
import type {
  FlowNodeAction,
  FlowNodeTimer,
  FlowPending,
  NullableFlowNode,
} from "@/api/types/flow";
import { SET_PROPERTY_KEY_PLACEHOLDER } from "@/lib/constants/flows";

export type ValidationSeverity = "error" | "warning";

export interface ValidationIssue {
  severity: ValidationSeverity;
  message: string;
}

const TRIGGER_KEY = "__triggers__";

function isConditionValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function validateTrigger(trigger: AutomationTrigger): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const conditions = trigger.conditions ?? [];

  if (trigger.type !== AutomationTriggerType.formSubmission && conditions.length === 0) {
    issues.push({ severity: "error", message: "A trigger rule is required" });
    return issues;
  }

  if (conditions.some((condition) => !condition.operators?.length)) {
    issues.push({ severity: "error", message: "Trigger operators are required" });
    return issues;
  }

  if (trigger.type === AutomationTriggerType.incomingMessage) {
    const operator = conditions[0]?.operators?.[0];
    // "Any message" mode uses `$exists`, which needs no keyword value.
    if (operator?.op !== "$exists" && isConditionValueEmpty(operator?.value)) {
      issues.push({
        severity: "error",
        message: "At least one incoming message keyword is required",
      });
    }
  }

  if (
    trigger.type === AutomationTriggerType.addedToList ||
    trigger.type === AutomationTriggerType.removedFromList
  ) {
    const value = conditions[0]?.operators?.[0]?.value;
    if (isConditionValueEmpty(value)) {
      issues.push({ severity: "error", message: "A list is required for this trigger" });
    }
  }

  if (trigger.type === AutomationTriggerType.customerEvent) {
    const hasEmptyOperator = conditions.some((condition) =>
      condition.operators.some((operator) =>
        operator.op === "$exists"
          ? operator.value === undefined
          : isConditionValueEmpty(operator.value),
      ),
    );
    if (hasEmptyOperator) {
      issues.push({ severity: "error", message: "Customer event trigger values are required" });
    }
  }

  return issues;
}

function validateNode(node: NullableFlowNode, issues: Map<string, ValidationIssue[]>) {
  if (!node || node.type === "root") {
    if (node?.type === "root") {
      for (const child of node.children) {
        validateNode(child, issues);
      }
    }
    return;
  }

  const nodeIssues: ValidationIssue[] = [];

  switch (node.type) {
    case "timer": {
      const timer = node as FlowNodeTimer;
      const options = timer.options;
      // A timer is valid in either mode: a relative `interval` or an absolute
      // `until` anchor date (resolved from the trigger event at runtime).
      const isUntil = !!options && "until" in options;
      const hasInterval = !!options && "interval" in options && !!options.interval;
      const hasUntilDate =
        isUntil && options.until?.date !== undefined && options.until?.date !== "";
      if (!hasInterval && !hasUntilDate) {
        nodeIssues.push({
          severity: "warning",
          message: isUntil
            ? "Timer requires an event date to wait until"
            : "Timer interval is required (days, hours, minutes, or seconds)",
        });
      }
      break;
    }

    case "action": {
      const action = node as FlowNodeAction;
      for (const a of action.options?.actions ?? []) {
        if (a.type === "outgoing_message") {
          const opts = a.options as Record<string, any>;
          if (opts?.protocol === "email") {
            const hasDoc = !!opts.email?.templateDoc;
            const hasBody = typeof opts.body === "string" && opts.body.trim().length > 0;
            if (!hasDoc && !hasBody) {
              nodeIssues.push({ severity: "error", message: "Email content is required" });
            }
            const directTo = opts.email?.to?.email;
            if (directTo != null) {
              const trimmed = typeof directTo === "string" ? directTo.trim() : "";
              if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                nodeIssues.push({
                  severity: "error",
                  message: "A valid custom recipient email is required",
                });
              }
            }
          } else {
            const hasBody = typeof opts?.body === "string" && opts.body.trim().length > 0;
            const hasTemplate = typeof opts?.template === "string" && opts.template.length > 0;
            const hasMedia = Array.isArray(opts?.media) && opts.media.length > 0;
            if (!hasBody && !hasTemplate && !hasMedia) {
              nodeIssues.push({
                severity: "error",
                message: "Message body or attachment is required",
              });
            }
          }
        }
        if (a.type === "outgoing_message_multichannel") {
          const opts = a.options as Partial<SendMessageMultichannelActionOptions> | undefined;
          const smsBody = opts?.sms?.body;
          const hasSmsBody = typeof smsBody === "string" && smsBody.trim().length > 0;
          const hasSmsMedia = Array.isArray(opts?.sms?.media) && opts.sms.media.length > 0;
          if (!hasSmsBody && !hasSmsMedia) {
            nodeIssues.push({
              severity: "error",
              message: "Text message body or attachment is required",
            });
          }
          const subject = opts?.email?.subject;
          if (typeof subject !== "string" || !subject.trim()) {
            nodeIssues.push({ severity: "error", message: "Email subject is required" });
          }
          const emailBody = opts?.email?.body;
          if (typeof emailBody !== "string" || !emailBody.trim()) {
            nodeIssues.push({ severity: "error", message: "Email body is required" });
          }
          if (opts?.fallbackTask) {
            const title = opts.fallbackTask.title;
            if (typeof title !== "string" || !title.trim()) {
              nodeIssues.push({ severity: "error", message: "A fallback task title is required" });
            }
          }
        }
        if (a.type === "webhook") {
          const url = (a.options as Record<string, any>)?.url;
          if (!url) {
            nodeIssues.push({ severity: "error", message: "A valid URL is required" });
          } else {
            try {
              new URL(url);
            } catch {
              nodeIssues.push({ severity: "error", message: "A valid URL is required" });
            }
          }
        }
        if (a.type === "add_to_list" || a.type === "remove_from_list") {
          const list = (a.options as Record<string, any>)?.list;
          if (!list) {
            nodeIssues.push({ severity: "error", message: "A list must be selected" });
          }
        }
        if (a.type === "add_tags" || a.type === "remove_tags") {
          const tags = (a.options as Record<string, any>)?.tags;
          const hasTags = Array.isArray(tags) && tags.some((tag) => `${tag}`.trim().length > 0);
          if (!hasTags) {
            nodeIssues.push({ severity: "error", message: "At least one tag is required" });
          }
        }
        if (a.type === "create_task") {
          const title = (a.options as Record<string, any>)?.title;
          if (typeof title !== "string" || !title.trim()) {
            nodeIssues.push({ severity: "error", message: "A task title is required" });
          }
        }
        if (a.type === "set_customer_property") {
          const key = (a.options as Record<string, any>)?.key;
          if (!key || key === SET_PROPERTY_KEY_PLACEHOLDER) {
            nodeIssues.push({ severity: "error", message: "Property key is required" });
          }
        }
      }
      break;
    }

    case "filter": {
      if (!node.options?.rules?.length) {
        nodeIssues.push({
          severity: "warning",
          message: "No rules configured \u2014 all contacts will pass through",
        });
      }
      break;
    }

    case "conditional": {
      if (!node.options?.rules?.length) {
        nodeIssues.push({
          severity: "warning",
          message: 'No rules configured \u2014 all contacts will match "Yes" branch',
        });
      }
      break;
    }

    case "branch": {
      for (const child of node.children) {
        if (child && child.type !== "filter") {
          nodeIssues.push({ severity: "error", message: "Branch children must be filter nodes" });
          break;
        }
      }
      break;
    }
  }

  if (nodeIssues.length) {
    issues.set(node.id, nodeIssues);
  }

  if ("children" in node && Array.isArray(node.children)) {
    for (const child of node.children) {
      validateNode(child, issues);
    }
  }
}

export function validateFlow(pending: FlowPending | undefined): Map<string, ValidationIssue[]> {
  const issues = new Map<string, ValidationIssue[]>();
  if (!pending) return issues;

  if (!pending.triggers?.length) {
    issues.set(TRIGGER_KEY, [
      { severity: "error", message: "A trigger is required to activate this flow" },
    ]);
  } else {
    const triggerIssues = validateTrigger(pending.triggers[0] as AutomationTrigger);
    if (triggerIssues.length) {
      issues.set(TRIGGER_KEY, triggerIssues);
    }
  }

  validateNode(pending.node, issues);

  return issues;
}

export function hasErrors(issues: Map<string, ValidationIssue[]>): boolean {
  for (const nodeIssues of issues.values()) {
    if (nodeIssues.some((i) => i.severity === "error")) return true;
  }
  return false;
}

export function getAllIssueMessages(issues: Map<string, ValidationIssue[]>): string[] {
  const messages: string[] = [];
  for (const nodeIssues of issues.values()) {
    for (const issue of nodeIssues) {
      if (issue.severity === "error") messages.push(issue.message);
    }
  }
  return messages;
}

export { TRIGGER_KEY };
